// src/Component/SideData/Overview/AiAgentWorkFlow/LeadGenIndex.js
import React, { useState, useEffect, useCallback, useRef } from "react";
import styles from "./LeadGenIndex.module.css";
import {
  FaAngleDown,
  FaLink,
  FaSave,
  FaRoute,
  FaSearch,
  FaCode,
  FaArrowRight,
  FaSync,
  FaExclamationTriangle,
  FaTimes,
  FaUndo,
  FaCheckCircle,
  FaExclamationCircle,
  FaKeyboard,
  FaInfoCircle,
  FaChevronDown,
} from "react-icons/fa";

const backend_api_base_url = `${process.env.REACT_APP_SL_BE_BASE_ENV_PART}/${process.env.REACT_APP_STAGE_ENV}`;

const TYPE_CONVERSIONS = {
  string: { label: "String", icon: "📝" },
  number: { label: "Number", icon: "🔢" },
  integer: { label: "Integer", icon: "🔢" },
  float: { label: "Float", icon: "💧" },
  boolean: { label: "Boolean", icon: "✓" },
  date: { label: "Date", icon: "📅" },
  datetime: { label: "DateTime", icon: "🕐" },
  array: { label: "Array", icon: "📋" },
  object: { label: "Object", icon: "📦" },
};

const COMPAT = {
  EXACT: "exact",
  UNDER: "under",
  OVER: "over",
  MISSING: "missing",
};

const COMPAT_META = {
  [COMPAT.EXACT]: { label: "Exact", icon: "✅", color: "#48bb78" },
  [COMPAT.UNDER]: { label: "Under", icon: "⚠️", color: "#ed8936" },
  [COMPAT.OVER]: { label: "Over", icon: "🔵", color: "#4299e1" },
  [COMPAT.MISSING]: { label: "Missing", icon: "❌", color: "#e53e3e" },
};

function renderTemplatePreview(template, fields) {
  if (!template || typeof template !== "string") return "";
  try {
    return template.replace(/\{\{\s*([^}]+?)\s*\}\}/g, (_, expr) => {
      const path = expr.trim().split(".");
      let cur = fields;
      for (const p of path) {
        if (cur && typeof cur === "object" && p in cur) cur = cur[p];
        else return "";
      }
      if (cur === undefined || cur === null) return "";
      if (typeof cur === "object") return JSON.stringify(cur);
      return String(cur);
    });
  } catch {
    return "";
  }
}

function classifyPreviewOutput(rendered) {
  if (!rendered) return { kind: "empty" };
  const s = rendered.trim();
  if (!s) return { kind: "empty" };
  const first = s[0];
  const looksJson =
    first === "{" ||
    first === "[" ||
    first === '"' ||
    s === "null" ||
    s === "true" ||
    s === "false" ||
    /^-?\d+(\.\d+)?$/.test(s);
  if (!looksJson) return { kind: "string", value: s };
  try {
    const parsed = JSON.parse(s);
    return { kind: "json", value: parsed };
  } catch {
    return { kind: "string", value: s };
  }
}

export default function LeadGenIndex({ appData }) {
  const log = useCallback((message, data) => {
    console.log(`[mission::orchestration] ${message}`, data ?? "");
  }, []);

  const appNameRef = useRef(appData?.app_name);
  const regionRef = useRef(appData?.region?.toLowerCase());

  const [botForms, setBotForms] = useState([]);
  const [loadingForms, setLoadingForms] = useState(true);
  const [loadingConfigs, setLoadingConfigs] = useState(true);
  const [error, setError] = useState(null);
  const [kbId, setKbId] = useState(null);

  const [apiConfigs, setApiConfigs] = useState([]);
  const [chains, setChains] = useState([]);
  const [formMappings, setFormMappings] = useState({});
  const [strictAuthPerForm, setStrictAuthPerForm] = useState({});
  const [mappingSource, setMappingSource] = useState({});

  const [selectedConfigPerForm, setSelectedConfigPerForm] = useState({});
  const [selectedChainPerForm, setSelectedChainPerForm] = useState({});
  const [savedConfigPerForm, setSavedConfigPerForm] = useState({});
  const [savedChainPerForm, setSavedChainPerForm] = useState({});

  const [fieldMappings, setFieldMappings] = useState({});
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [currentMappingForm, setCurrentMappingForm] = useState(null);

  const [mappingMode, setMappingMode] = useState("api");
  const [resolvedSpecOverride, setResolvedSpecOverride] = useState(null);

  const [tempMappings, setTempMappings] = useState({
    path: {},
    query: {},
    payload: {},
  });
  const [activeTab, setActiveTab] = useState("payload");
  const [availableParams, setAvailableParams] = useState({
    path: [],
    query: [],
    payload: [],
  });
  const [tempStrictAuth, setTempStrictAuth] = useState(true);

  const [expandedTemplateRows, setExpandedTemplateRows] = useState({});

  const [validationErrors, setValidationErrors] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showKeyboardShortcuts, setShowKeyboardShortcuts] = useState(false);

  // ⭐ P2: inline 403 banner on save. Rendered inside the mapping modal
  // when the caller tries to save a form mapping that references an
  // unattached tenant api_config / chain. Same shape and same trigger
  // as the banner in IntentIndex, but rendered inline in the modal body
  // so the operator can navigate away to attach and come back without
  // losing the mapping in progress.
  const [attachmentRefusal, setAttachmentRefusal] = useState(null);

  const getChainRootApiSpec = useCallback(
    (chain) => {
      if (!chain?.root?.api_config_id) return null;
      const cfg = apiConfigs.find(
        (c) => c.api_config_id === chain.root.api_config_id,
      );
      return cfg?.api_config || null;
    },
    [apiConfigs],
  );

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "s") {
        e.preventDefault();
        if (showMappingModal) {
          handleSaveAll();
        }
      }
      if (e.key === "Escape" && showMappingModal) {
        handleCancelMapping();
      }
      if (e.key === "?" && !showMappingModal) {
        setShowKeyboardShortcuts(!showKeyboardShortcuts);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [showMappingModal, tempMappings]);

  useEffect(() => {
    if (currentMappingForm) {
      const saved = fieldMappings[currentMappingForm] || {
        path: {},
        query: {},
        payload: {},
      };
      const current = tempMappings;
      const mappingsChanged =
        JSON.stringify(saved.path) !== JSON.stringify(current.path) ||
        JSON.stringify(saved.query) !== JSON.stringify(current.query) ||
        JSON.stringify(saved.payload) !== JSON.stringify(current.payload);

      const savedStrict =
        strictAuthPerForm[currentMappingForm] !== undefined
          ? strictAuthPerForm[currentMappingForm]
          : true;
      const strictChanged = savedStrict !== tempStrictAuth;

      setHasUnsavedChanges(mappingsChanged || strictChanged);
    }
  }, [
    tempMappings,
    tempStrictAuth,
    currentMappingForm,
    fieldMappings,
    strictAuthPerForm,
  ]);

  const extractAllForms = (node, depth = 0, maxDepth = 10) => {
    if (!node || typeof node !== "object" || depth > maxDepth) return [];
    let foundForms = [];
    if (node.type === "form_type" && node.text) {
      const form = {
        form_name: node.text,
        fields: [],
        uuid: node.uuid || `form-${Date.now()}-${Math.random()}`,
        original_node: node,
        depth: depth,
      };
      if (Array.isArray(node.fields)) {
        node.fields.forEach((field) => {
          if (field.type === "field_type") {
            const fieldName =
              field.field ||
              field.text ||
              "field_" + Date.now();
            form.fields.push({
              label: field.text || "Untitled Field",
              type: field.fieldType || "text",
              required: field.required || false,
              placeholder: field.placeholder || "",
              uuid: field.uuid,
              field_name: fieldName,
            });
          }
        });
      }
      foundForms.push(form);
    }
    const possibleChildrenProps = [
      "subQuestions",
      "answers",
      "fields",
      "children",
      "nodes",
      "items",
      "options",
      "sub_nodes",
    ];
    for (const prop of possibleChildrenProps) {
      if (Array.isArray(node[prop])) {
        node[prop].forEach((child) => {
          foundForms = [
            ...foundForms,
            ...extractAllForms(child, depth + 1, maxDepth),
          ];
        });
      }
    }
    Object.keys(node).forEach((key) => {
      if (
        typeof node[key] === "object" &&
        node[key] !== null &&
        !possibleChildrenProps.includes(key) &&
        key !== "uuid" &&
        key !== "text" &&
        key !== "type"
      ) {
        foundForms = [
          ...foundForms,
          ...extractAllForms(node[key], depth + 1, maxDepth),
        ];
      }
    });
    return foundForms;
  };

  useEffect(() => {
    const fetchBotContent = async () => {
      const appName = appData?.app_name;
      const region = appData?.region?.toLowerCase();
      appNameRef.current = appName;
      regionRef.current = region;

      try {
        setLoadingForms(true);
        setError(null);
        const token = localStorage.getItem("token");
        if (!token) throw new Error("No token found in localStorage");
        const payload = JSON.parse(atob(token.split(".")[1]));
        const tenantId = payload.tenant_id;

        const response = await fetch(
          `https://${region}.autobot2.${backend_api_base_url}/get_bot_content?app_name=${appName}&tenant_id=${tenantId}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: token,
            },
          },
        );
        if (!response.ok)
          throw new Error(`Bot content fetch failed: ${response.status}`);
        const data = await response.json();
        const extractedKbId = data?.bot_payload?.kb_id;
        setKbId(extractedKbId);

        const botPayload = data?.bot_payload;
        let allForms = [];
        if (botPayload) {
          const topLevelSections = [
            "FAQ",
            "WelcomeMessage",
            "FallbackMessage",
            "Intents",
            "Contexts",
          ];
          for (const section of topLevelSections) {
            if (botPayload[section]) {
              allForms = [
                ...allForms,
                ...extractAllForms(botPayload[section]),
              ];
            }
          }
          allForms = [...allForms, ...extractAllForms(botPayload)];
        }
        const uniqueForms = [];
        const uuidSet = new Set();
        for (const form of allForms) {
          if (!uuidSet.has(form.uuid)) {
            uuidSet.add(form.uuid);
            uniqueForms.push(form);
          }
        }
        setBotForms(uniqueForms);

        const initialMappings = {};
        uniqueForms.forEach((form) => {
          initialMappings[form.uuid] = { path: {}, query: {}, payload: {} };
        });
        setFieldMappings(initialMappings);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoadingForms(false);
      }
    };

    if (appData?.app_name) {
      fetchBotContent();
    } else {
      setLoadingForms(false);
    }
  }, [appData?.app_name, appData?.region]);

  const fetchConfigsAndMappings = useCallback(async () => {
    setLoadingConfigs(true);
    const token = localStorage.getItem("token");
    if (!token || !appNameRef.current) {
      setLoadingConfigs(false);
      return;
    }
    const region = regionRef.current;
    const tenantId = (() => {
      try {
        const p = JSON.parse(atob(token.split(".")[1]));
        return p.tenant_id;
      } catch {
        return null;
      }
    })();

    try {
      const appNameParam = encodeURIComponent(appNameRef.current);

      const apiConfigResp = await fetch(
        `https://${region}.autobot2.${backend_api_base_url}/api_config?app_name=${appNameParam}&include_tenant=true`,
        { headers: { Authorization: token } },
      );
      if (apiConfigResp.ok) {
        const data = await apiConfigResp.json();
        const allConfigs = data.configs || [];
        const regularConfigs = allConfigs.filter(
          (cfg) => cfg.api_type === "REGULAR",
        );
        setApiConfigs(regularConfigs);
      }

      const chainsResp = await fetch(
        `https://${region}.autobot2.${backend_api_base_url}/chain?app_name=${appNameParam}&include_tenant=true`,
        { headers: { Authorization: token } },
      );
      if (chainsResp.ok) {
        const data = await chainsResp.json();
        setChains(data.chains || []);
      }

      const mappingsResp = await fetch(
        `https://${region}.autobot2.${backend_api_base_url}/get_form_api_config?tenant_id=${tenantId}&app_name=${appNameRef.current}&fetch_mapping_as_well=true`,
        { headers: { Authorization: token } },
      );
      if (mappingsResp.ok) {
        const data = await mappingsResp.json();
        console.log("[LeadGen] Mappings response:", data);

        const mapping = {};
        const strictAuthMap = {};
        const initConfigSelect = {};
        const initChainSelect = {};
        const initSavedConfig = {};
        const initSavedChain = {};
        const sourceMap = {};

        const configs = data.configs || [];
        for (const cfg of configs) {
          const formUuid = cfg.form_uuid;

          mapping[formUuid] = {
            api_config_id: cfg.api_config_id || "",
            chain_id: cfg.chain_id || "",
            resolved_config: cfg.api_config || null,
            new_table_mapping: cfg.new_table_mapping || null,
          };

          strictAuthMap[formUuid] =
            cfg.integration_enabled === undefined
              ? true
              : !!cfg.integration_enabled;
          sourceMap[formUuid] = cfg.from_new_table
            ? "new_table"
            : "legacy_table";

          if (
            cfg.path_param_mappings ||
            cfg.query_param_mappings ||
            cfg.payload_param_mappings
          ) {
            setFieldMappings((prev) => ({
              ...prev,
              [formUuid]: {
                path: cfg.path_param_mappings || {},
                query: cfg.query_param_mappings || {},
                payload: cfg.payload_param_mappings || {},
              },
            }));
          }

          if (cfg.chain_id) {
            initChainSelect[formUuid] = cfg.chain_id;
            initConfigSelect[formUuid] = "";
            initSavedChain[formUuid] = cfg.chain_id;
            initSavedConfig[formUuid] = "";
          } else if (cfg.api_config_id) {
            initConfigSelect[formUuid] = cfg.api_config_id;
            initChainSelect[formUuid] = "";
            initSavedConfig[formUuid] = cfg.api_config_id;
            initSavedChain[formUuid] = "";
          } else {
            initConfigSelect[formUuid] = "";
            initChainSelect[formUuid] = "";
            initSavedConfig[formUuid] = "";
            initSavedChain[formUuid] = "";
          }
        }

        setFormMappings(mapping);
        setStrictAuthPerForm(strictAuthMap);
        setSelectedConfigPerForm((prev) => ({ ...prev, ...initConfigSelect }));
        setSelectedChainPerForm((prev) => ({ ...prev, ...initChainSelect }));
        setSavedConfigPerForm((prev) => ({ ...prev, ...initSavedConfig }));
        setSavedChainPerForm((prev) => ({ ...prev, ...initSavedChain }));
        setMappingSource(sourceMap);
      }
    } catch (err) {
      console.error("Failed to fetch configs/mappings", err);
    } finally {
      setLoadingConfigs(false);
    }
  }, []);

  useEffect(() => {
    fetchConfigsAndMappings();
  }, [fetchConfigsAndMappings]);

  const inferFieldType = (value) => {
    if (typeof value !== "string") {
      if (typeof value === "boolean") return "boolean";
      if (typeof value === "number")
        return Number.isInteger(value) ? "integer" : "float";
      if (value === null || value === undefined) return "string";
      return "string";
    }
    const t = value.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return "date";
    if (/^\d{2}:\d{2}(:\d{2})?$/.test(t)) return "string";
    if (/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(t)) return "datetime";
    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(t)) return "string";
    if (/^\+?[\d\s\-()]{7,}$/.test(t)) return "string";
    if (/^https?:\/\/\S+$/.test(t)) return "string";
    if (/^(true|false)$/i.test(t)) return "boolean";
    if (/^\d+$/.test(t)) return "integer";
    if (/^\d+\.\d+$/.test(t)) return "float";
    return "string";
  };

  const parseApiStructure = (apiConfig) => {
    if (!apiConfig)
      return { pathParams: [], queryParams: [], payloadParams: [] };

    const structure = { pathParams: [], queryParams: [], payloadParams: [] };

    const samplePayload = (() => {
      const oc = apiConfig.original_curl_config || {};
      if (oc.payload && typeof oc.payload === "object") return oc.payload;
      try {
        return JSON.parse(apiConfig.payload_template || "{}");
      } catch {
        return {};
      }
    })();

    const getByPath = (obj, dotted) => {
      const parts = dotted.split(".");
      let cur = obj;
      for (const p of parts) {
        if (cur && typeof cur === "object" && p in cur) cur = cur[p];
        else return undefined;
      }
      return cur;
    };

    const url = apiConfig.url || "";
    const pathMatches = url.match(/\{\{\s*([^}]+)\s*\}\}/g) || [];
    pathMatches.forEach((match) => {
      const param = match.replace(/\{\{\s*|\s*\}\}/g, "").trim();
      if (param && !structure.pathParams.some((p) => p.name === param)) {
        structure.pathParams.push({
          name: param,
          path: param,
          expectedType: "string",
          isStatic: false,
        });
      }
    });

    const queryParams = apiConfig.query_params || {};
    Object.keys(queryParams).forEach((key) => {
      const value = queryParams[key];
      if (typeof value === "string" && value.includes("{{")) {
        const matches = value.match(/\{\{\s*([^}]+)\s*\}\}/g) || [];
        matches.forEach((match) => {
          const param = match.replace(/\{\{\s*|\s*\}\}/g, "").trim();
          if (param && !structure.queryParams.some((p) => p.name === param)) {
            structure.queryParams.push({
              name: param,
              path: key,
              expectedType: inferFieldType(value),
              isStatic: false,
            });
          }
        });
      } else {
        if (!structure.queryParams.some((p) => p.name === key)) {
          structure.queryParams.push({
            name: key,
            path: key,
            expectedType: inferFieldType(value),
            isStatic: true,
          });
        }
      }
    });

    const payloadTemplate = apiConfig.payload_template || "{}";
    try {
      const templateObj =
        typeof payloadTemplate === "string"
          ? JSON.parse(payloadTemplate)
          : payloadTemplate;

      const extractKeys = (obj, prefix = "") => {
        if (typeof obj !== "object" || obj === null) return;
        Object.keys(obj).forEach((key) => {
          const fullKey = prefix ? `${prefix}.${key}` : key;
          const value = obj[key];
          if (typeof value === "string" && value.includes("{{")) {
            const matches = value.match(/\{\{\s*([^}]+)\s*\}\}/g) || [];
            matches.forEach((match) => {
              const param = match.replace(/\{\{\s*|\s*\}\}/g, "").trim();
              if (
                param &&
                !structure.payloadParams.some((p) => p.name === param)
              ) {
                const sample =
                  getByPath(samplePayload, param) ??
                  getByPath(samplePayload, fullKey);
                structure.payloadParams.push({
                  name: param,
                  path: fullKey,
                  template: value,
                  expectedType: inferFieldType(sample),
                  isStatic: false,
                });
              }
            });
          } else if (typeof value === "object" && value !== null) {
            extractKeys(value, fullKey);
          } else {
            if (!structure.payloadParams.some((p) => p.name === key)) {
              structure.payloadParams.push({
                name: key,
                path: fullKey,
                value: value,
                expectedType: inferFieldType(value),
                isStatic: true,
              });
            }
          }
        });
      };
      extractKeys(templateObj);
    } catch {
      const matches = payloadTemplate.match(/\{\{\s*([^}]+)\s*\}\}/g) || [];
      matches.forEach((match) => {
        const param = match.replace(/\{\{\s*|\s*\}\}/g, "").trim();
        if (param && !structure.payloadParams.some((p) => p.name === param)) {
          structure.payloadParams.push({
            name: param,
            path: param,
            template: match,
            expectedType: "string",
            isStatic: false,
          });
        }
      });
    }

    return structure;
  };

  const detectFieldType = (field) => {
    const name = field.field_name.toLowerCase();
    if (
      name.includes("id") ||
      name.includes("number") ||
      name.includes("count")
    )
      return "integer";
    if (
      name.includes("date") ||
      name.includes("time") ||
      name.includes("datetime")
    )
      return "datetime";
    if (name.includes("email")) return "string";
    if (name.includes("phone")) return "string";
    if (
      name.includes("price") ||
      name.includes("amount") ||
      name.includes("cost")
    )
      return "float";
    if (
      name.includes("active") ||
      name.includes("enabled") ||
      name.includes("is_")
    )
      return "boolean";
    if (field.type === "number" || field.type === "range") return "integer";
    if (field.type === "date") return "date";
    if (field.type === "datetime-local") return "datetime";
    if (field.type === "checkbox") return "boolean";
    return "string";
  };

  const typesCompatible = (fieldType, paramType) => {
    if (!paramType || paramType === "string") return true;
    if (paramType === "integer" || paramType === "number") {
      return (
        fieldType === "integer" ||
        fieldType === "number" ||
        fieldType === "float"
      );
    }
    if (paramType === "float") {
      return (
        fieldType === "integer" ||
        fieldType === "number" ||
        fieldType === "float"
      );
    }
    return fieldType === paramType;
  };

  const classifyParam = (param, formFields, currentMapping) => {
    const currentTemplate = (
      currentMapping?.[param.name]?.mapping_template || ""
    ).trim();
    if (currentTemplate) {
      return {
        compat: COMPAT.EXACT,
        candidates: [],
        chosen: currentMapping[param.name]?.field || "__template__",
        reason: "Custom mapping template provided",
      };
    }

    const mappedFieldName = currentMapping?.[param.name]?.field || null;
    if (mappedFieldName) {
      const mapped = formFields.find(
        (f) => f.field_name === mappedFieldName,
      );
      if (mapped) {
        const fieldType = detectFieldType(mapped);
        if (typesCompatible(fieldType, param.expectedType)) {
          return {
            compat: COMPAT.EXACT,
            candidates: [mapped],
            chosen: mapped.field_name,
            reason: `Manually mapped to "${mapped.field_name}"`,
          };
        }
        return {
          compat: COMPAT.UNDER,
          candidates: [mapped],
          chosen: mapped.field_name,
          reason: `Mapped, but type mismatch: form has ${fieldType}, param expects ${param.expectedType}`,
        };
      }
    }

    const paramName = param.name.toLowerCase();

    const exactNameMatches = formFields.filter(
      (f) => f.field_name.toLowerCase() === paramName,
    );
    const labelMatches = formFields.filter(
      (f) =>
        f.label.toLowerCase().includes(paramName) ||
        f.field_name.toLowerCase().includes(paramName),
    );

    const parentName = param.name.includes(".")
      ? param.name.split(".")[0].toLowerCase()
      : null;
    const parentMatch = parentName
      ? formFields.filter(
          (f) => f.field_name.toLowerCase() === parentName,
        )
      : [];

    if (
      exactNameMatches.length === 0 &&
      labelMatches.length === 0 &&
      parentMatch.length === 0
    ) {
      return {
        compat: COMPAT.MISSING,
        candidates: [],
        chosen: null,
        reason: "No form field supplies this parameter",
      };
    }

    if (exactNameMatches.length > 1) {
      return {
        compat: COMPAT.OVER,
        candidates: exactNameMatches,
        chosen: exactNameMatches[0].field_name,
        reason: `${exactNameMatches.length} form fields match this name — pick one`,
      };
    }

    if (exactNameMatches.length === 1) {
      const f = exactNameMatches[0];
      const fieldType = detectFieldType(f);
      if (typesCompatible(fieldType, param.expectedType)) {
        return {
          compat: COMPAT.EXACT,
          candidates: [f],
          chosen: f.field_name,
          reason: `Name and type compatible (${fieldType})`,
        };
      }
      return {
        compat: COMPAT.UNDER,
        candidates: [f],
        chosen: f.field_name,
        reason: `Name matches but type mismatch: form has ${fieldType}, param expects ${param.expectedType}`,
      };
    }

    if (labelMatches.length >= 1) {
      const f = labelMatches[0];
      return {
        compat: COMPAT.UNDER,
        candidates: labelMatches,
        chosen: f.field_name,
        reason: `Form field label matches "${param.name}" but field_name "${f.field_name}" differs`,
      };
    }

    if (parentMatch.length >= 1) {
      return {
        compat: COMPAT.UNDER,
        candidates: parentMatch,
        chosen: parentMatch[0].field_name,
        reason: `Form provides "${parentMatch[0].field_name}" but param needs nested "${param.name}"`,
      };
    }

    return {
      compat: COMPAT.MISSING,
      candidates: [],
      chosen: null,
      reason: "No compatible form field found",
    };
  };

  const classifyAllParams = (structure, formFields, mappings) => {
    const rows = [];
    const push = (loc, params) => {
      params.forEach((p) => {
        const cls = classifyParam(p, formFields, mappings[loc] || {});
        rows.push({ location: loc, param: p, ...cls });
      });
    };
    push(
      "payload",
      structure.payloadParams.filter((p) => !p.isStatic),
    );
    push(
      "query",
      structure.queryParams.filter((p) => !p.isStatic),
    );
    push("path", structure.pathParams);
    return rows;
  };

  const buildAutoMappings = (structure, formFields) => {
    const next = { path: {}, query: {}, payload: {} };

    const process = (loc, params) => {
      params
        .filter((p) => !p.isStatic)
        .forEach((p) => {
          const cls = classifyParam(p, formFields, next[loc]);
          if (cls.compat === COMPAT.MISSING) return;
          const chosen = cls.chosen;
          if (!chosen) return;
          next[loc][p.name] = {
            field: chosen,
            mapped_to: p.name,
            convert_to:
              p.expectedType && p.expectedType !== "string"
                ? p.expectedType
                : "string",
            mapping_template: "",
          };
        });
    };

    process("payload", structure.payloadParams);
    process("query", structure.queryParams);
    process("path", structure.pathParams);
    return next;
  };

  const handleClearAllMappings = () => {
    if (
      window.confirm(
        "Are you sure you want to clear all field mappings for this form?",
      )
    ) {
      setTempMappings({ path: {}, query: {}, payload: {} });
      setValidationErrors({});
      setHasUnsavedChanges(true);
    }
  };

  const openMappingModal = (formUuid, options = {}) => {
    const { specOverride = null, mode = "api" } = options;

    const form = botForms.find((f) => f.uuid === formUuid);
    if (!form) return;

    const existingMappings = fieldMappings[formUuid] || {
      path: {},
      query: {},
      payload: {},
    };

    let resolvedSpec = specOverride;
    if (!resolvedSpec) {
      const selectedConfigId =
        selectedConfigPerForm[formUuid] || savedConfigPerForm[formUuid];
      const selectedConfig = apiConfigs.find(
        (c) => c.api_config_id === selectedConfigId,
      );
      resolvedSpec = selectedConfig?.api_config || null;
    }

    if (!resolvedSpec) {
      console.warn(
        "[LeadGenIndex] openMappingModal called but no spec could be resolved for form",
        formUuid,
      );
    }

    const structure = parseApiStructure(resolvedSpec || {});

    const hasAnyExisting =
      Object.keys(existingMappings.path || {}).length > 0 ||
      Object.keys(existingMappings.query || {}).length > 0 ||
      Object.keys(existingMappings.payload || {}).length > 0;

    let nextMappings;
    if (hasAnyExisting) {
      nextMappings = {
        path: existingMappings.path || {},
        query: existingMappings.query || {},
        payload: existingMappings.payload || {},
      };
    } else {
      nextMappings = buildAutoMappings(structure, form.fields);
    }

    console.log("[LeadGenIndex] openMappingModal:", {
      formUuid,
      mode,
      hasSpec: !!resolvedSpec,
      specUrl: resolvedSpec?.url,
      specMethod: resolvedSpec?.method,
      payloadParamCount: structure.payloadParams.filter((p) => !p.isStatic)
        .length,
      queryParamCount: structure.queryParams.filter((p) => !p.isStatic)
        .length,
      pathParamCount: structure.pathParams.length,
      formFieldCount: form.fields.length,
      autoMappedPayload: Object.keys(nextMappings.payload).length,
      autoMappedQuery: Object.keys(nextMappings.query).length,
      autoMappedPath: Object.keys(nextMappings.path).length,
    });

    setTempMappings(nextMappings);
    setResolvedSpecOverride(resolvedSpec);
    setMappingMode(mode);

    const savedStrict =
      strictAuthPerForm[formUuid] !== undefined
        ? strictAuthPerForm[formUuid]
        : true;
    setTempStrictAuth(savedStrict);

    setAvailableParams({
      path: structure.pathParams,
      query: structure.queryParams,
      payload: structure.payloadParams.filter((p) => !p.isStatic),
    });

    setCurrentMappingForm(formUuid);

    if (structure.payloadParams.filter((p) => !p.isStatic).length > 0)
      setActiveTab("payload");
    else if (structure.queryParams.filter((p) => !p.isStatic).length > 0)
      setActiveTab("query");
    else if (structure.pathParams.length > 0) setActiveTab("path");
    else setActiveTab("payload");

    setValidationErrors({});
    setHasUnsavedChanges(false);
    setExpandedTemplateRows({});
    setAttachmentRefusal(null);
    setShowMappingModal(true);
  };

  const handleParamMappingChange = (location, paramName, key, value) => {
    setTempMappings((prev) => ({
      ...prev,
      [location]: {
        ...prev[location],
        [paramName]: {
          ...(prev[location][paramName] || {
            field: "",
            mapped_to: paramName,
            convert_to: "string",
            mapping_template: "",
          }),
          [key]: value,
        },
      },
    }));
    if (validationErrors[paramName]) {
      setValidationErrors((prev) => {
        const n = { ...prev };
        delete n[paramName];
        return n;
      });
    }
  };

  const handleAutoMap = (formFields) => {
    let resolvedSpec = resolvedSpecOverride;
    if (!resolvedSpec) {
      const selectedConfigId =
        selectedConfigPerForm[currentMappingForm] ||
        savedConfigPerForm[currentMappingForm];
      const selectedConfig = apiConfigs.find(
        (c) => c.api_config_id === selectedConfigId,
      );
      resolvedSpec = selectedConfig?.api_config || null;
    }
    const structure = parseApiStructure(resolvedSpec || {});
    const next = buildAutoMappings(structure, formFields);

    ["path", "query", "payload"].forEach((loc) => {
      Object.keys(next[loc]).forEach((paramName) => {
        const existing = tempMappings[loc]?.[paramName];
        if (existing?.mapping_template) {
          next[loc][paramName].mapping_template = existing.mapping_template;
        }
      });
    });

    setTempMappings(next);
    setValidationErrors({});
  };

  const validateMappings = (mappings, formFields, structure) => {
    const rows = classifyAllParams(structure, formFields, mappings);
    const errors = {};
    let blockingCount = 0;

    rows.forEach((r) => {
      if (r.compat === COMPAT.MISSING) {
        blockingCount += 1;
        errors[`${r.location}:${r.param.name}`] =
          `[${r.location}] "${r.param.name}" — ${r.reason}`;
      }
    });

    const warnings = rows
      .filter((r) => r.compat === COMPAT.UNDER || r.compat === COMPAT.OVER)
      .map((r) => `[${r.location}] "${r.param.name}" — ${r.reason}`);

    return { errors, blockingCount, warnings, rows };
  };

  const handleSaveAll = async () => {
        const formUuid = currentMappingForm;
    if (!formUuid) return;

    const token = localStorage.getItem("token");
    const region = regionRef.current;

    const selectedConfigId =
      selectedConfigPerForm[formUuid] || savedConfigPerForm[formUuid];
    const selectedChainId =
      selectedChainPerForm[formUuid] || savedChainPerForm[formUuid];

    if (mappingMode === "chain" && !selectedChainId) {
      alert("Please select a Chain first");
      return;
    }
    if (mappingMode === "api" && !selectedConfigId) {
      alert("Please select an API config first");
      return;
    }

    const form = botForms.find((f) => f.uuid === formUuid);

    let resolvedSpec = resolvedSpecOverride;
    if (!resolvedSpec) {
      const selectedConfig = apiConfigs.find(
        (c) => c.api_config_id === selectedConfigId,
      );
      resolvedSpec = selectedConfig?.api_config || null;
    }
    const structure = parseApiStructure(resolvedSpec || {});

    const { errors, blockingCount, warnings } = validateMappings(
      tempMappings,
      form.fields,
      structure,
    );

    if (blockingCount > 0) {
      setValidationErrors(errors);
      const msgs = Object.values(errors);
      alert(
        `Cannot save — ${blockingCount} parameter(s) are missing or need a compatible form field:\n\n` +
          msgs.slice(0, 10).join("\n") +
          (msgs.length > 10 ? `\n…and ${msgs.length - 10} more` : ""),
      );
      return;
    }

    if (warnings.length > 0) {
      if (
        !window.confirm(
          `Warnings (non-blocking):\n\n${warnings.join("\n")}\n\nDo you want to continue?`,
        )
      )
        return;
    }

    const totalMapped = Object.values(tempMappings).reduce(
      (acc, m) => acc + Object.keys(m || {}).length,
      0,
    );
    const expectedMappings =
      structure.payloadParams.filter((p) => !p.isStatic).length +
      structure.queryParams.filter((p) => !p.isStatic).length +
      structure.pathParams.length;

    if (totalMapped === 0 && expectedMappings > 0) {
      const proceed = window.confirm(
        `⚠️ This API expects ${expectedMappings} mapped parameter(s), but no mappings are set.\n\n` +
          `Saving will store an empty mapping, and the API will receive raw form fields.\n\n` +
          `Continue anyway?`,
      );
      if (!proceed) return;
    }

    try {
      const integration_enabled = tempStrictAuth;

      const cleanMappings = (mappings) => {
        const result = {};
        Object.entries(mappings).forEach(([paramName, mapping]) => {
          const template = (mapping.mapping_template || "").trim();
          const hasTemplate = !!template;
          const hasScalar =
            mapping.mapped_to && mapping.mapped_to.trim() && mapping.field;

          if (!hasTemplate && !hasScalar) return;

          const entry = {
            field: mapping.field || paramName,
            mapped_to: mapping.mapped_to || paramName,
            convert_to: mapping.convert_to || "string",
          };
          if (hasTemplate) {
            entry.mapping_template = template;
          }
          result[entry.field] = entry;
        });
        return result;
      };

      const mappingPayload = {
        tenant_id: (() => {
          try {
            const p = JSON.parse(atob(token.split(".")[1]));
            return p.tenant_id;
          } catch {
            return null;
          }
        })(),
        app_name: appNameRef.current,
        form_uuid: formUuid,
        scope: "app",
        integration_enabled: integration_enabled,
        path_param_mappings: cleanMappings(tempMappings.path || {}),
        query_param_mappings: cleanMappings(tempMappings.query || {}),
        payload_param_mappings: cleanMappings(tempMappings.payload || {}),
      };

      if (mappingMode === "chain") {
        mappingPayload.chain_id = selectedChainId;
        mappingPayload.api_config_id = null;
      } else {
        mappingPayload.api_config_id = selectedConfigId;
        mappingPayload.chain_id = null;
      }

      console.log("[LeadGenIndex] handleSaveAll payload:", mappingPayload);

      const mappingResp = await fetch(
        `https://${region}.autobot2.${backend_api_base_url}/form_api_config`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify(mappingPayload),
        },
      );

      // ⭐ P2: inline 403 banner on save. Backend returns `assert_attached`
      // when the caller tries to map a form to a tenant-scoped resource
      // the app hasn't attached. Surface it as an inline banner INSIDE
      // the mapping modal, so the operator can navigate away to attach
      // (in APIConfig / ChainApis) and come back without losing the
      // mapping they've built. The modal stays open and the refusal
      // state clears on the next save attempt.
      if (!mappingResp.ok) {
        let body = {};
        try {
          body = await mappingResp.json();
        } catch (_) {}
        const msg =
          body?.error ||
          body?.detail ||
          `Save failed (HTTP ${mappingResp.status})`;

        const isAttachmentRefusal =
          mappingResp.status === 403 ||
          (typeof msg === "string" &&
            msg.includes("has not opted in to tenant-scoped"));

        if (isAttachmentRefusal) {
          setAttachmentRefusal({
            resourceType:
              mappingMode === "chain" ? "chain" : "api_config",
            resourceId:
              mappingMode === "chain" ? selectedChainId : selectedConfigId,
            message: msg,
          });
          return;
        }

        throw new Error(msg);
      }

      const savedResp = await mappingResp.json().catch(() => ({}));
      console.log("[LeadGenIndex] handleSaveAll response:", savedResp);

      setFieldMappings((prev) => ({ ...prev, [formUuid]: tempMappings }));
      setStrictAuthPerForm((prev) => ({
        ...prev,
        [formUuid]: integration_enabled,
      }));

      if (mappingMode === "chain") {
        setSavedChainPerForm((prev) => ({
          ...prev,
          [formUuid]: selectedChainId,
        }));
        setSavedConfigPerForm((prev) => ({ ...prev, [formUuid]: "" }));
        setSelectedConfigPerForm((prev) => ({ ...prev, [formUuid]: "" }));
      } else {
        setSavedConfigPerForm((prev) => ({
          ...prev,
          [formUuid]: selectedConfigId,
        }));
        setSavedChainPerForm((prev) => ({ ...prev, [formUuid]: "" }));
        setSelectedChainPerForm((prev) => ({ ...prev, [formUuid]: "" }));
      }

      setMappingSource((prev) => ({ ...prev, [formUuid]: "new_table" }));
      setHasUnsavedChanges(false);
      setValidationErrors({});
      setAttachmentRefusal(null);

      alert("Form connection and field mappings saved successfully!");
      setShowMappingModal(false);
      setCurrentMappingForm(null);
      setTempMappings({ path: {}, query: {}, payload: {} });
      setTempStrictAuth(true);
      setResolvedSpecOverride(null);
      setMappingMode("api");
      setExpandedTemplateRows({});

      fetchConfigsAndMappings();
    } catch (e) {
      alert(`Failed to save: ${e.message}`);
    }
  };

  const handleCancelMapping = () => {
    if (hasUnsavedChanges) {
      if (
        !window.confirm(
          "You have unsaved changes. Are you sure you want to close?",
        )
      )
        return;
    }
    const formUuid = currentMappingForm;

    if (formUuid) {
      if (mappingMode === "chain" && !savedChainPerForm[formUuid]) {
        setSelectedChainPerForm((prev) => ({ ...prev, [formUuid]: "" }));
      }
      if (mappingMode === "api" && !savedConfigPerForm[formUuid]) {
        setSelectedConfigPerForm((prev) => ({ ...prev, [formUuid]: "" }));
      }
    }

    setShowMappingModal(false);
    setCurrentMappingForm(null);
    setTempMappings({ path: {}, query: {}, payload: {} });
    setTempStrictAuth(true);
    setValidationErrors({});
    setHasUnsavedChanges(false);
    setResolvedSpecOverride(null);
    setMappingMode("api");
    setExpandedTemplateRows({});
    setAttachmentRefusal(null);
  };

  const handleDetach = async (formUuid) => {
    if (!formUuid) return;

    const form = botForms.find((f) => f.uuid === formUuid);
    const formLabel = form?.form_name || "this form";

    const confirmed = window.confirm(
      `⚠️ Remove configuration for "${formLabel}"?\n\n` +
        `This disconnects the form from its API or Chain and clears all field mappings.\n` +
        `The API/Chain itself is NOT deleted — it stays available in its own section.\n\n` +
        `This action cannot be undone.`,
    );
    if (!confirmed) return;

    const token = localStorage.getItem("token");
    const region = regionRef.current;
    const tenantId = (() => {
      try {
        const p = JSON.parse(atob(token.split(".")[1]));
        return p.tenant_id;
      } catch {
        return null;
      }
    })();

    if (!tenantId) {
      alert("Unable to determine tenant_id from session.");
      return;
    }

    try {
      const url =
        `https://${region}.autobot2.${backend_api_base_url}` +
        `/delete_form_api_config?tenant_id=${encodeURIComponent(tenantId)}` +
        `&form_uuid=${encodeURIComponent(formUuid)}`;

      const resp = await fetch(url, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
      });

      const data = await resp.json().catch(() => ({}));

      if (!resp.ok || data.success === false) {
        throw new Error(data.error || `Delete failed (${resp.status})`);
      }

      setFormMappings((prev) => {
        const n = { ...prev };
        delete n[formUuid];
        return n;
      });
      setSavedConfigPerForm((prev) => ({ ...prev, [formUuid]: "" }));
      setSavedChainPerForm((prev) => ({ ...prev, [formUuid]: "" }));
      setSelectedConfigPerForm((prev) => ({ ...prev, [formUuid]: "" }));
      setSelectedChainPerForm((prev) => ({ ...prev, [formUuid]: "" }));
      setFieldMappings((prev) => ({
        ...prev,
        [formUuid]: { path: {}, query: {}, payload: {} },
      }));
      setMappingSource((prev) => {
        const n = { ...prev };
        delete n[formUuid];
        return n;
      });
      setStrictAuthPerForm((prev) => ({ ...prev, [formUuid]: true }));

      if (currentMappingForm === formUuid) {
        setShowMappingModal(false);
        setCurrentMappingForm(null);
        setTempMappings({ path: {}, query: {}, payload: {} });
        setTempStrictAuth(true);
        setResolvedSpecOverride(null);
        setMappingMode("api");
      }

      alert(`✅ Configuration removed for "${formLabel}".`);
      fetchConfigsAndMappings();
    } catch (e) {
      alert(`Failed to remove configuration: ${e.message}`);
    }
  };

  const handleConfigSelect = (formUuid, configId) => {
    setSelectedConfigPerForm((prev) => ({ ...prev, [formUuid]: configId }));

    if (configId) {
      setSelectedChainPerForm((prev) => ({ ...prev, [formUuid]: "" }));

      const cfg = apiConfigs.find((c) => c.api_config_id === configId);
      const spec = cfg?.api_config || null;

      if (!spec) {
        console.warn(
          "[LeadGenIndex] Could not resolve api_config for id",
          configId,
        );
      }

      openMappingModal(formUuid, { specOverride: spec, mode: "api" });
    } else {
      setShowMappingModal(false);
      setCurrentMappingForm(null);
      setResolvedSpecOverride(null);
      setMappingMode("api");
    }
  };

  const handleChainSelect = (formUuid, chainId) => {
    setSelectedChainPerForm((prev) => ({ ...prev, [formUuid]: chainId }));

    if (!chainId) {
      setShowMappingModal(false);
      setCurrentMappingForm(null);
      setResolvedSpecOverride(null);
      setMappingMode("api");
      return;
    }

    setSelectedConfigPerForm((prev) => ({ ...prev, [formUuid]: "" }));

    const chain = chains.find((c) => c.chain_id === chainId);
    const rootSpec = getChainRootApiSpec(chain);

    if (!rootSpec) {
      alert(
        `⚠️ Could not resolve the root API for "${
          chain?.name || "this chain"
        }".\n\nThe chain's root API config may have been deleted, or it is not yet loaded. Please refresh and try again.`,
      );
      setSelectedChainPerForm((prev) => ({ ...prev, [formUuid]: "" }));
      return;
    }

    openMappingModal(formUuid, { specOverride: rootSpec, mode: "chain" });
  };

  const handleModifySettings = (form) => {
    const formUuid = form.uuid;

    const chainId =
      selectedChainPerForm[formUuid] || savedChainPerForm[formUuid];
    const configId =
      selectedConfigPerForm[formUuid] || savedConfigPerForm[formUuid];

    let spec = null;
    let mode = "api";

    if (chainId) {
      const chain = chains.find((c) => c.chain_id === chainId);
      spec = getChainRootApiSpec(chain);
      mode = "chain";
    } else if (configId) {
      const cfg = apiConfigs.find((c) => c.api_config_id === configId);
      spec = cfg?.api_config || null;
      mode = "api";
    }

    if (!spec) {
      console.warn(
        "[LeadGenIndex] Modify Settings: could not resolve spec for form",
        formUuid,
      );
    }

    openMappingModal(formUuid, { specOverride: spec, mode });
  };

  const renderKeyboardShortcuts = () => {
    if (!showKeyboardShortcuts) return null;
    return (
      <div
        className={styles.shortcutsOverlay}
        onClick={() => setShowKeyboardShortcuts(false)}
      >
        <div
          className={styles.shortcutsContent}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={styles.shortcutsHeader}>
            <h3>
              <FaKeyboard /> How this works
            </h3>
            <button
              className={styles.shortcutsCloseBtn}
              onClick={() => setShowKeyboardShortcuts(false)}
            >
              ×
            </button>
          </div>
          <div className={styles.shortcutsBody}>
            <div className={styles.shortcutRow}>
              <span className={styles.shortcutKey}>⌘/Ctrl + S</span>
              <span className={styles.shortcutDesc}>Save field mappings</span>
            </div>
            <div className={styles.shortcutRow}>
              <span className={styles.shortcutKey}>Esc</span>
              <span className={styles.shortcutDesc}>Close this panel</span>
            </div>
            <div className={styles.shortcutRow}>
              <span className={styles.shortcutKey}>?</span>
              <span className={styles.shortcutDesc}>
                Show / hide this panel
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderMappingModal = () => {
    if (!showMappingModal || !currentMappingForm) return null;

    const form = botForms.find((f) => f.uuid === currentMappingForm);
    if (!form) return null;

    let selectedConfig = null;
    let resolvedSpec = resolvedSpecOverride;

    if (!resolvedSpec) {
      const selectedConfigId =
        selectedConfigPerForm[currentMappingForm] ||
        savedConfigPerForm[currentMappingForm];
      selectedConfig = apiConfigs.find(
        (c) => c.api_config_id === selectedConfigId,
      );
      resolvedSpec = selectedConfig?.api_config || null;
    } else {
      const chainId =
        selectedChainPerForm[currentMappingForm] ||
        savedChainPerForm[currentMappingForm];
      const chain = chains.find((c) => c.chain_id === chainId);
      const rootCfgId = chain?.root?.api_config_id;
      const rootCfg = rootCfgId
        ? apiConfigs.find((c) => c.api_config_id === rootCfgId)
        : null;
      selectedConfig = rootCfg || { api_config: resolvedSpec };
    }

    const structure = parseApiStructure(resolvedSpec || {});
    const rows = classifyAllParams(structure, form.fields, tempMappings);

    const rowsByLoc = {
      payload: rows.filter((r) => r.location === "payload"),
      query: rows.filter((r) => r.location === "query"),
      path: rows.filter((r) => r.location === "path"),
    };

    const counts = rows.reduce(
      (acc, r) => {
        acc[r.compat] = (acc[r.compat] || 0) + 1;
        return acc;
      },
      {
        [COMPAT.EXACT]: 0,
        [COMPAT.UNDER]: 0,
        [COMPAT.OVER]: 0,
        [COMPAT.MISSING]: 0,
      },
    );

    const totalParams = rows.length;
    const satisfiedParams = counts[COMPAT.EXACT];
    const progressPct =
      totalParams > 0 ? (satisfiedParams / totalParams) * 100 : 0;

    const hasPathParams = rowsByLoc.path.length > 0;
    const hasQueryParams = rowsByLoc.query.length > 0;
    const hasPayloadParams = rowsByLoc.payload.length > 0;

    const currentRows = rowsByLoc[activeTab] || [];

    const allFormFields = form.fields;
    const usedFieldNames = new Set(rows.map((r) => r.chosen).filter(Boolean));
    const unusedFormFields = allFormFields.filter(
      (f) => !usedFieldNames.has(f.field_name),
    );

    const sampleFields = {};
    form.fields.forEach((f) => {
      const t = detectFieldType(f);
      if (t === "integer" || t === "float") sampleFields[f.field_name] = 1;
      else if (t === "boolean") sampleFields[f.field_name] = true;
      else if (t === "date") sampleFields[f.field_name] = "2026-01-01";
      else if (t === "datetime")
        sampleFields[f.field_name] = "2026-01-01T00:00:00";
      else sampleFields[f.field_name] = `<${f.field_name}>`;
    });

    const toggleTemplateRow = (location, paramName) => {
      const key = `${location}:${paramName}`;
      setExpandedTemplateRows((prev) => ({
        ...prev,
        [key]: !prev[key],
      }));
    };

    const formatRow = (row) => {
      const { param, compat, reason, chosen } = row;
      const meta = COMPAT_META[compat];
      const mapping = tempMappings[row.location]?.[param.name] || {};
      const currentMappedField = mapping.field || chosen || "";
      const currentConvert =
        mapping.convert_to ||
        (param.expectedType !== "string" ? param.expectedType : "string");
      const currentTemplate = mapping.mapping_template || "";

      const rowKey = `${row.location}:${param.name}`;
      const isExpanded = !!expandedTemplateRows[rowKey];

      const previewRendered = currentTemplate
        ? renderTemplatePreview(currentTemplate, sampleFields)
        : "";
      const previewClass = previewRendered
        ? classifyPreviewOutput(previewRendered)
        : { kind: "empty" };

      const hasTemplate = !!currentTemplate.trim();

      return (
        <div
          key={rowKey}
          className={`${styles.mappingRow} ${hasTemplate ? styles.mappingRowHasTemplate : ""}`}
          style={{ borderLeft: `4px solid ${meta.color}` }}
        >
          <div className={styles.mappingRowMain}>
            <div className={styles.mappingColumn}>
              <div className={styles.paramHeader}>
                <span className={styles.fieldName}>{param.name}</span>
                {param.expectedType && (
                  <span className={styles.fieldType}>
                    ({param.expectedType})
                  </span>
                )}
                <span
                  className={styles.compatBadge}
                  style={{ background: meta.color }}
                  title={reason}
                >
                  {meta.icon} {meta.label}
                </span>
              </div>
              <div className={styles.compatReason}>{reason}</div>
            </div>

            <div className={styles.mappingColumn}>
              <FaArrowRight className={styles.mappingArrow} />
            </div>

            <div className={styles.mappingColumn}>
              <div className={styles.selectWrapperSmall}>
                <select
                  className={styles.mappingSelect}
                  value={currentMappedField}
                  onChange={(e) =>
                    handleParamMappingChange(
                      row.location,
                      param.name,
                      "field",
                      e.target.value,
                    )
                  }
                >
                  <option value="">-- pick a form field --</option>
                  {form.fields.map((f) => (
                    <option key={f.field_name} value={f.field_name}>
                      {f.label} ({f.field_name})
                    </option>
                  ))}
                </select>
                <FaAngleDown className={styles.selectIconSmall} />
              </div>
            </div>

            <div className={styles.mappingColumn}>
              <div className={styles.selectWrapperSmall}>
                <select
                  className={styles.conversionSelect}
                  value={currentConvert}
                  onChange={(e) =>
                    handleParamMappingChange(
                      row.location,
                      param.name,
                      "convert_to",
                      e.target.value,
                    )
                  }
                >
                  {Object.entries(TYPE_CONVERSIONS).map(([key, val]) => (
                    <option key={key} value={key}>
                      {val.icon} {val.label}
                    </option>
                  ))}
                </select>
                <FaAngleDown className={styles.selectIconSmall} />
              </div>
            </div>

            <div className={styles.mappingColumn}>
              <span
                className={`${styles.locationBadge} ${styles[row.location]}`}
              >
                {row.location === "path"
                  ? "🔗 URL Path"
                  : row.location === "query"
                    ? "🔍 Query"
                    : "📦 Body"}
              </span>
            </div>

            <div className={styles.mappingColumn}>
              <button
                type="button"
                onClick={() => toggleTemplateRow(row.location, param.name)}
                className={`${styles.templateToggleBtn} ${hasTemplate ? styles.templateToggleBtnActive : ""}`}
                title="Use a custom template instead of the simple mapping"
              >
                {hasTemplate ? "📝 ✓" : "📝"}
              </button>
            </div>
          </div>

          {isExpanded && (
            <div className={styles.templateEditor}>
              <div className={styles.templateEditorHint}>
                Custom mapping template. When non-empty, this overrides the
                simple mapping above. Use <code>{"{{ field_name }}"}</code> to
                insert form field values.
              </div>

              <textarea
                value={currentTemplate}
                onChange={(e) =>
                  handleParamMappingChange(
                    row.location,
                    param.name,
                    "mapping_template",
                    e.target.value,
                  )
                }
                placeholder={`e.g. {"id": {{ room_id }}}  OR  {{ check_in }}T00:00:00  OR  [{{ tag1 }}, {{ tag2 }}]`}
                rows={2}
                className={styles.templateTextarea}
              />

              <div className={styles.templateFieldChips}>
                <span className={styles.templateFieldChipsLabel}>
                  Form fields:
                </span>
                {form.fields.map((f) => (
                  <button
                    key={f.field_name}
                    type="button"
                    onClick={() => {
                      const cur = currentTemplate || "";
                      const next = cur + `{{ ${f.field_name} }}`;
                      handleParamMappingChange(
                        row.location,
                        param.name,
                        "mapping_template",
                        next,
                      );
                    }}
                    className={styles.templateFieldChip}
                    title={`Click to insert {{ ${f.field_name} }}`}
                  >
                    {f.field_name}
                  </button>
                ))}
              </div>

              {currentTemplate.trim() && (
                <div
                  className={`${styles.templatePreview} ${
                    previewClass.kind === "json"
                      ? styles.templatePreviewJson
                      : previewClass.kind === "string"
                        ? styles.templatePreviewString
                        : styles.templatePreviewEmpty
                  }`}
                >
                  <div className={styles.templatePreviewLabel}>
                    Preview ({previewClass.kind}
                    {previewClass.kind === "json" ? " → object/array" : ""})
                  </div>
                  <code className={styles.templatePreviewValue}>
                    {previewRendered || "(empty)"}
                  </code>
                </div>
              )}
            </div>
          )}
        </div>
      );
    };

    return (
      <div className={styles.modalOverlay}>
        <div className={styles.modalContent}>
          <div className={styles.modalHeader}>
            <h3>
              Field Mapping: {form.form_name}
              {mappingMode === "chain" && (
                <span
                  style={{
                    marginLeft: "10px",
                    fontSize: "11px",
                    fontWeight: "600",
                    color: "#2b6cb0",
                    backgroundColor: "#ebf8ff",
                    padding: "2px 10px",
                    borderRadius: "10px",
                    border: "1px solid #90cdf4",
                  }}
                >
                  🔗 via Chain (root API)
                </span>
              )}
            </h3>
            <div className={styles.modalHeaderActions}>
              {hasUnsavedChanges && (
                <span className={styles.unsavedIndicator}>
                  ● Unsaved changes
                </span>
              )}
              <button
                className={styles.clearAllBtn}
                onClick={handleClearAllMappings}
                title="Clear all mappings"
              >
                <FaUndo /> Clear All
              </button>
              <button
                className={styles.modalCloseBtn}
                onClick={handleCancelMapping}
              >
                ×
              </button>
            </div>
          </div>

          <div className={styles.modalBody}>
            {/* ⭐ P2: inline 403 banner on save. Renders only when a save
                tripped the attachment guard. Dismissible; the operator
                clicks it away after navigating to APIConfig / ChainApis
                to attach the referenced resource. The modal remains
                open so the in-progress mapping is preserved. */}
            {attachmentRefusal && (
              <div
                role="alert"
                style={{
                  marginBottom: 12,
                  padding: "12px 16px",
                  backgroundColor: "#fff8e1",
                  border: "1px solid #fde68a",
                  borderLeft: "3px solid #d97706",
                  borderRadius: 6,
                  color: "#78350f",
                  fontSize: 13,
                  lineHeight: 1.55,
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 10,
                }}
              >
                <span style={{ fontSize: 18, flexShrink: 0 }}>⚠️</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>
                    Can&apos;t save this mapping — the{" "}
                    {attachmentRefusal.resourceType === "chain"
                      ? "chain"
                      : "API config"}{" "}
                    isn&apos;t attached to this app.
                  </div>
                  <div>{attachmentRefusal.message}</div>
                  <div style={{ marginTop: 6, color: "#92400e" }}>
                    Open{" "}
                    <strong>
                      {attachmentRefusal.resourceType === "chain"
                        ? "ChainApis → Tenant Level"
                        : "API Config → Tenant Level"}
                    </strong>{" "}
                    and attach this{" "}
                    {attachmentRefusal.resourceType === "chain"
                      ? "chain"
                      : "config"}{" "}
                    to this app, then click Save again. Your mapping is
                    preserved.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setAttachmentRefusal(null)}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#78350f",
                    cursor: "pointer",
                    fontSize: 16,
                    lineHeight: 1,
                    padding: 4,
                  }}
                  aria-label="Dismiss"
                >
                  ×
                </button>
              </div>
            )}

            <p className={styles.mappingInfo}>
              The API decides what it needs. For each parameter below, pick the
              form field that supplies it, or click <strong>📝 Template</strong>{" "}
              to build a custom mapping. Save is blocked while any parameter is{" "}
              <strong>missing</strong> or has a <strong>type mismatch</strong>.
              {mappingMode === "chain" && (
                <>
                  {" "}
                  These are the parameters of the chain's{" "}
                  <strong>root API</strong>.
                </>
              )}
            </p>

            {selectedConfig && (
              <div className={styles.selectedConfigInfo}>
                <div className={styles.configUrl}>
                  <strong>Connected API:</strong>{" "}
                  {selectedConfig.api_config?.url} (
                  {selectedConfig.api_config?.method})
                </div>

                <div className={styles.configStructure}>
                  <span className={styles.structureLabel}>
                    Compatibility:
                  </span>
                  <span
                    className={styles.structureBadge}
                    style={{
                      background: COMPAT_META.exact.color,
                      color: "white",
                    }}
                  >
                    ✅ Exact: {counts.exact}
                  </span>
                  <span
                    className={styles.structureBadge}
                    style={{
                      background: COMPAT_META.under.color,
                      color: "white",
                    }}
                  >
                    ⚠️ Under: {counts.under}
                  </span>
                  <span
                    className={styles.structureBadge}
                    style={{
                      background: COMPAT_META.over.color,
                      color: "white",
                    }}
                  >
                    🔵 Over: {counts.over}
                  </span>
                  <span
                    className={styles.structureBadge}
                    style={{
                      background: COMPAT_META.missing.color,
                      color: "white",
                    }}
                  >
                    ❌ Missing: {counts.missing}
                  </span>
                </div>

                <div className={styles.mappingProgress}>
                  <span>
                    Progress: {satisfiedParams}/{totalParams} params exact
                  </span>
                  <div className={styles.progressBar}>
                    <div
                      className={styles.progressFill}
                      style={{ width: `${progressPct}%` }}
                    />
                  </div>
                </div>

                <div
                  className={styles.strictAuthSection}
                  style={{ marginTop: "12px" }}
                >
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={tempStrictAuth}
                      onChange={(e) => setTempStrictAuth(e.target.checked)}
                      className={styles.checkbox}
                    />
                    <strong>Require User Token</strong>
                    <span
                      style={{
                        marginLeft: "6px",
                        color: "#666",
                        fontSize: "13px",
                        cursor: "help",
                      }}
                      title="When enabled, the system will require a valid user authentication token when making this API call."
                    >
                      <FaInfoCircle size={13} />
                    </span>
                    <small className={styles.helperText}>
                      (require token authentication)
                    </small>
                  </label>
                </div>
              </div>
            )}

            <div className={styles.tabs}>
              <button
                className={`${styles.tab} ${activeTab === "payload" ? styles.tabActive : ""} ${!hasPayloadParams ? styles.tabDisabled : ""}`}
                onClick={() => hasPayloadParams && setActiveTab("payload")}
                disabled={!hasPayloadParams}
              >
                <FaCode /> In Request Body
                {hasPayloadParams && (
                  <span className={styles.tabBadge}>
                    {rowsByLoc.payload.length}
                    {rowsByLoc.payload.some(
                      (r) =>
                        r.compat === COMPAT.MISSING ||
                        r.compat === COMPAT.UNDER,
                    ) && <span className={styles.requiredAsterisk}>*</span>}
                  </span>
                )}
              </button>
              <button
                className={`${styles.tab} ${activeTab === "query" ? styles.tabActive : ""} ${!hasQueryParams ? styles.tabDisabled : ""}`}
                onClick={() => hasQueryParams && setActiveTab("query")}
                disabled={!hasQueryParams}
              >
                <FaSearch /> In URL Query String
                {hasQueryParams && (
                  <span className={styles.tabBadge}>
                    {rowsByLoc.query.length}
                    {rowsByLoc.query.some(
                      (r) =>
                        r.compat === COMPAT.MISSING ||
                        r.compat === COMPAT.UNDER,
                    ) && <span className={styles.requiredAsterisk}>*</span>}
                  </span>
                )}
              </button>
              <button
                className={`${styles.tab} ${activeTab === "path" ? styles.tabActive : ""} ${!hasPathParams ? styles.tabDisabled : ""}`}
                onClick={() => hasPathParams && setActiveTab("path")}
                disabled={!hasPathParams}
              >
                <FaRoute /> In URL Path
                {hasPathParams && (
                  <span className={styles.tabBadge}>
                    {rowsByLoc.path.length}
                    {rowsByLoc.path.some(
                      (r) =>
                        r.compat === COMPAT.MISSING ||
                        r.compat === COMPAT.UNDER,
                    ) && <span className={styles.requiredAsterisk}>*</span>}
                  </span>
                )}
              </button>
            </div>

            <button
              className={styles.autoMapBtn}
              onClick={() => handleAutoMap(form.fields)}
            >
              <FaSync /> Auto-Map Fields
            </button>

            <div className={styles.mappingTable}>
              <div className={styles.mappingHeader}>
                <div className={styles.mappingColumn}>API Parameter</div>
                <div className={styles.mappingColumn}>→</div>
                <div className={styles.mappingColumn}>Form Field</div>
                <div className={styles.mappingColumn}>Convert To</div>
                <div className={styles.mappingColumn}>Location</div>
                <div className={styles.mappingColumn}>Template</div>
              </div>
              {currentRows.length === 0 ? (
                <div
                  style={{
                    padding: 16,
                    color: "#718096",
                    textAlign: "center",
                  }}
                >
                  No parameters in this section.
                </div>
              ) : (
                currentRows.map(formatRow)
              )}
            </div>

            {unusedFormFields.length > 0 && (
              <div
                className={styles.structureSection}
                style={{ marginTop: 12 }}
              >
                <h4>
                  <FaExclamationTriangle /> Unused form fields (
                  {unusedFormFields.length})
                </h4>
                <p style={{ fontSize: 12, color: "#718096" }}>
                  These form fields do not map to any parameter. They will not
                  be sent. That's fine if intentional.
                </p>
                               <div className={styles.paramList}>
                  {unusedFormFields.map((f) => (
                    <span
                      key={f.field_name}
                      className={`${styles.paramTag} ${styles.static}`}
                    >
                      {f.label} ({f.field_name})
                    </span>
                  ))}
                </div>
              </div>
            )}

            {Object.keys(validationErrors).length > 0 && (
              <div className={styles.validationSummary}>
                <strong>Blocking issues:</strong>
                <ul>
                  {Object.entries(validationErrors).map(([k, v]) => (
                    <li key={k}>{v}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className={styles.modalActions}>
              <div className={styles.modalActionsLeft}>
                <span className={styles.shortcutHint}>
                  <FaKeyboard /> ⌘/Ctrl+S to save · Esc to close
                </span>
              </div>
              <div className={styles.modalActionsRight}>
                <button
                  className={styles.cancelBtn}
                  onClick={handleCancelMapping}
                >
                  Cancel
                </button>
                <button
                  className={styles.saveMappingsBtn}
                  onClick={handleSaveAll}
                  disabled={counts.missing > 0}
                  title={
                    counts.missing > 0
                      ? "Resolve missing parameters first"
                      : ""
                  }
                >
                  <FaSave /> Save All
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const isLoading = loadingForms || loadingConfigs;
  if (isLoading)
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}></div>
        <h3 className={styles.loadingTitle}>Loading configuration...</h3>
        <p className={styles.loadingText}>
          Fetching your forms{loadingForms ? "..." : " ✓"}
        </p>
        <p className={styles.loadingText}>
          Loading configurations{loadingConfigs ? "..." : " ✓"}
        </p>
      </div>
    );
  if (error)
    return (
      <div className={styles.errorContainer}>
        <span className={styles.errorIcon}>⚠️</span>
        Error loading forms: {error}
      </div>
    );

  return (
    <div className={styles.leadGenWrapper}>
      {renderKeyboardShortcuts()}

      <div className={styles.headerSection}>
        <h2 className={styles.title}>Leadgen Workflow</h2>
        <p className={styles.subtitle}>
          Turn each lead form into a guided, multi-step experience.
        </p>
        <div className={styles.headerActions}>
          <button
            className={styles.shortcutsToggleBtn}
            onClick={() => setShowKeyboardShortcuts(true)}
          >
            <FaKeyboard /> How this works ⓘ
          </button>
          <div className={styles.dataSourceIndicator}>
            <span className={styles.dataSourceLabel}>Where leads live:</span>
            <span className={styles.dataSourceValue}>
              ✅ Saved in your portal, scoped per app
            </span>
          </div>
        </div>
      </div>

      <div className={styles.formsSection}>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>Your Lead Forms</h3>
          <p className={styles.sectionSubtitle}>
            For each form, pick the API or Chain that should handle it. The API
            already knows what to do — how to respond, what screens to show,
            what the visitor can click. This screen is just where you wire up
            the inputs: which form field fills which API parameter.
          </p>
          <p className={styles.sectionSubtitle}>
            Once mapped, submitting the form triggers your API, and the visitor
            sees exactly the screens and options you designed in{" "}
            <em>Agent &amp; Servers → API Config</em> or <em>ChainApis</em>.
          </p>
        </div>

        {botForms.length === 0 ? (
          <div className={styles.emptyState}>
            <p>
              <strong>No lead forms yet.</strong>
            </p>
            <p>
              Create your first lead form in <em>LeadGen &amp; FAQ</em>, then
              come back here to connect it to one of your APIs or Chains.
            </p>
          </div>
        ) : (
          <div className={styles.formsGrid}>
            {botForms.map((form) => {
              const mapping = formMappings[form.uuid] || {};
              const hasSavedConfig = !!savedConfigPerForm[form.uuid];
              const hasSavedChain = !!savedChainPerForm[form.uuid];
              const isMapped = hasSavedConfig || hasSavedChain;

              const resolvedConfig = mapping.resolved_config || null;
              const mappedChainId =
                savedChainPerForm[form.uuid] || mapping.chain_id || null;

              const strictAuth =
                strictAuthPerForm[form.uuid] !== undefined
                  ? strictAuthPerForm[form.uuid]
                  : true;

              const selectedConfigId = selectedConfigPerForm[form.uuid] || "";
              const selectedChainId = selectedChainPerForm[form.uuid] || "";

              const mappedChain = chains.find(
                (c) => c.chain_id === mappedChainId,
              );
              const isConfigSelected = !!selectedConfigId;

              const hasMappings =
                fieldMappings[form.uuid] &&
                (Object.keys(fieldMappings[form.uuid].path || {}).length > 0 ||
                  Object.keys(fieldMappings[form.uuid].query || {}).length >
                    0 ||
                  Object.keys(fieldMappings[form.uuid].payload || {}).length >
                    0);

              return (
                <div
                  key={form.uuid}
                  className={`${styles.formCard} ${isMapped ? styles.formCardMapped : styles.formCardUnmapped}`}
                >
                  <div className={styles.formCardHeader}>
                    <div className={styles.formTitleSection}>
                      <h4 className={styles.formTitle}>{form.form_name}</h4>
                      {isMapped && (
                        <span className={styles.badgeMapped}>Connected</span>
                      )}
                      {hasMappings && (
                        <span className={styles.badgeFieldsMapped}>
                          Fields Mapped
                        </span>
                      )}
                    </div>
                    {!isMapped && (
                      <span className={styles.badgeUnmapped}>
                        Not Connected
                      </span>
                    )}
                  </div>

                  <div className={styles.formFieldsSection}>
                    <div className={styles.formFieldsLabel}>
                      Form fields
                      <div className={styles.formFieldsList}>
                        {form.fields.map((f, idx) => (
                          <span key={idx} className={styles.fieldTag}>
                            {f.field_name || f.label}{" "}
                            {f.required && (
                              <span className={styles.requiredStar}>*</span>
                            )}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className={styles.apiConfigSection}>
                    <label className={styles.formLabel}>
                      Handle submissions with an API:
                    </label>
                    <div className={styles.selectWrapper}>
                      <select
                        value={selectedConfigId}
                        onChange={(e) =>
                          handleConfigSelect(form.uuid, e.target.value)
                        }
                        className={`${styles.selectInput} ${styles.customSelect}`}
                      >
                        <option value="">-- Select an API --</option>
                        {apiConfigs.filter(
                          (c) => c.scope === "app" || !c.scope,
                        ).length > 0 && (
                          <optgroup label="App Level (this app)">
                            {apiConfigs
                              .filter((c) => c.scope === "app" || !c.scope)
                              .map((cfg) => (
                                <option
                                  key={cfg.api_config_id}
                                  value={cfg.api_config_id}
                                >
                                  {cfg.api_config?.url || cfg.api_config_id} (
                                  {cfg.api_config?.method || "GET"})
                                </option>
                              ))}
                          </optgroup>
                        )}
                        {apiConfigs.filter((c) => c.scope === "tenant").length >
                          0 && (
                          <optgroup label="Shared (tenant-wide)">
                            {apiConfigs
                              .filter((c) => c.scope === "tenant")
                              .map((cfg) => (
                                <option
                                  key={cfg.api_config_id}
                                  value={cfg.api_config_id}
                                >
                                  {cfg.api_config?.url || cfg.api_config_id} (
                                  {cfg.api_config?.method || "GET"})
                                </option>
                              ))}
                          </optgroup>
                        )}
                      </select>
                      <FaAngleDown className={styles.selectIcon} />
                    </div>
                    {isConfigSelected && (
                      <span className={styles.configSelectedIndicator}>
                        Opening field mapping...
                      </span>
                    )}
                  </div>

                  <div className={styles.chainConfigSection}>
                    <label className={styles.formLabel}>
                      Or handle submissions with a Chain:
                    </label>
                    <span
                      style={{
                        marginLeft: "6px",
                        color: "#666",
                        fontSize: "13px",
                        cursor: "help",
                      }}
                      title="A Chain runs several of your APIs in sequence. Field mapping below uses the first API in the chain (the 'root')."
                    >
                      <FaInfoCircle size={13} />
                    </span>
                    <div className={styles.selectWrapper}>
                      <select
                        value={selectedChainId}
                        onChange={(e) =>
                          handleChainSelect(form.uuid, e.target.value)
                        }
                        className={`${styles.selectInput} ${styles.customSelect}`}
                      >
                        <option value="">-- Select a Chain --</option>
                        {chains.filter((c) => c.scope === "app" || !c.scope)
                          .length > 0 && (
                          <optgroup label="App Level (this app)">
                            {chains
                              .filter((c) => c.scope === "app" || !c.scope)
                              .map((chain) => {
                                const chainStatus =
                                  chain.enabled !== false
                                    ? "Active"
                                    : "Disabled";
                                const branchCount = Object.keys(
                                  chain.root?.branches || {},
                                ).length;
                                return (
                                  <option
                                    key={chain.chain_id}
                                    value={chain.chain_id}
                                  >
                                    {chain.name || "Unnamed Chain"} (
                                    {branchCount} branch(es)) – {chainStatus}
                                  </option>
                                );
                              })}
                          </optgroup>
                        )}
                        {chains.filter((c) => c.scope === "tenant").length >
                          0 && (
                          <optgroup label="Shared (tenant-wide)">
                            {chains
                              .filter((c) => c.scope === "tenant")
                              .map((chain) => {
                                const chainStatus =
                                  chain.enabled !== false
                                    ? "Active"
                                    : "Disabled";
                                const branchCount = Object.keys(
                                  chain.root?.branches || {},
                                ).length;
                                return (
                                  <option
                                    key={chain.chain_id}
                                    value={chain.chain_id}
                                  >
                                    {chain.name || "Unnamed Chain"} (
                                    {branchCount} branch(es)) – {chainStatus}
                                  </option>
                                );
                              })}
                          </optgroup>
                        )}
                      </select>
                      <FaAngleDown className={styles.selectIcon} />
                    </div>
                  </div>

                  {isMapped && (
                    <div className={styles.mappedConfigInfo}>
                      <strong>This form is connected to:</strong>
                      <div className={styles.configRow}>
                        <span className={styles.configLabel}>
                          Requires User Token:
                        </span>
                        <span className={styles.configValue}>
                          {strictAuth ? "🔐 Yes" : "🔓 No"}
                        </span>
                      </div>
                      {resolvedConfig &&
                        Object.keys(resolvedConfig).length > 0 && (
                          <>
                            <div className={styles.configRow}>
                              <span className={styles.configLabel}>
                                URL:
                              </span>
                              <span className={styles.configValue}>
                                {resolvedConfig.url}
                              </span>
                            </div>
                            <div className={styles.configRow}>
                              <span className={styles.configLabel}>
                                Method:
                              </span>
                              <span className={styles.configValue}>
                                {resolvedConfig.method}
                              </span>
                            </div>

                            {hasMappings && (
                              <div className={styles.fieldMappingsSummary}>
                                <span className={styles.configLabel}>
                                  Field Mappings:
                                </span>
                                <div className={styles.mappingTags}>
                                  {Object.entries(
                                    fieldMappings[form.uuid] || {},
                                  ).map(([tab, mappings]) =>
                                    Object.entries(mappings).map(
                                      ([field, mapping]) => (
                                        <span
                                          key={`${tab}-${field}`}
                                          className={styles.mappingTag}
                                        >
                                          {field} →{" "}
                                          {mapping.mapped_to || field}
                                          <span className={styles.mappingTab}>
                                            ({tab})
                                          </span>
                                          {mapping.mapping_template && (
                                            <span
                                              style={{
                                                marginLeft: 4,
                                                fontSize: 10,
                                                color: "#6b46c1",
                                                backgroundColor: "#faf5ff",
                                                padding: "0 4px",
                                                borderRadius: 6,
                                              }}
                                              title={
                                                mapping.mapping_template
                                              }
                                            >
                                              📝
                                            </span>
                                          )}
                                        </span>
                                      ),
                                    ),
                                  )}
                                </div>
                              </div>
                            )}

                            <div
                              style={{
                                display: "flex",
                                gap: "8px",
                                flexWrap: "wrap",
                                marginTop: "8px",
                              }}
                            >
                              <button
                                className={styles.editMappingBtn}
                                onClick={() => handleModifySettings(form)}
                                title="Modify this form's settings"
                              >
                                Modify Settings
                              </button>
                              <button
                                className={styles.deleteMappingBtn}
                                onClick={() => handleDetach(form.uuid)}
                                title="Detach this form from its API/Chain"
                              >
                                Remove Configuration
                              </button>
                            </div>

                            <details className={styles.configDetails}>
                              <summary className={styles.configSummary}>
                                Full Config
                              </summary>
                              <pre className={styles.configPre}>
                                {JSON.stringify(resolvedConfig, null, 2)}
                              </pre>
                            </details>
                          </>
                        )}
                      {mappedChainId && mappedChain && (
                        <div className={styles.configRow}>
                          <span className={styles.configLabel}>Chain:</span>
                          <span className={styles.configValue}>
                            {mappedChain.name || "Unnamed"} (
                            {
                              Object.keys(mappedChain.root?.branches || {})
                                .length
                            }{" "}
                            branch(es),{" "}
                            {mappedChain.enabled !== false
                              ? "Active"
                              : "Disabled"}
                            )
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {renderMappingModal()}
    </div>
  );
}